import { PlusIcon, TrashIcon } from '@heroicons/react/24/solid'
import { zodResolver } from '@hookform/resolvers/zod'
import FormRow from 'components/Form/FormRow'
import TextAreaFieldHookFormWrapper from 'components/Form/Inputs/TextAreaField/wrappers/hookForm'
import SubmitButton from 'components/Form/SubmitButton'
import ModalBody from 'components/Modal/ModalBody'
import ModalFooter from 'components/Modal/ModalFooter'
import UrlModal from 'components/Modal/UrlModal'
import { menusQueryKey } from 'containers/menus/queries'
import { recipesQueryKey, useEditRecipeMutation, useGetSingleRecipeQuery } from 'containers/recipes/queries'
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { queryClient } from 'utils/queryClient'
import * as z from 'zod'

type Inputs = {
    method: { value: string }[]
}

const schema = z.object({
    method: z.array(
        z.object({
            value: z.string()
        })
    )
})

export function EditRecipeMethod() {
    const navigate = useNavigate()
    const { recipeId } = useParams()

    const { data: singleRecipeData } = useGetSingleRecipeQuery(recipeId ?? '')
    const { mutateAsync: editRecipe } = useEditRecipeMutation()

    const methods = useForm<Inputs>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: {
            method: singleRecipeData?.method?.map((step) => ({ value: step })) || []
        }
    })

    const { append, remove, fields } = useFieldArray<Inputs>({
        name: 'method',
        control: methods.control
    })

    const {
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = methods

    if (!singleRecipeData) {
        return null
    }

    const onSubmit: SubmitHandler<Inputs> = async ({ method }) => {
        await editRecipe(
            {
                recipeId: recipeId || '',
                attributes: {
                    name: singleRecipeData.name,
                    method: method.map((field) => field.value),
                    recipe_category: singleRecipeData.recipe_category?.name ?? null,
                    prep_time: singleRecipeData.prep_time,
                    cook_time: singleRecipeData.cook_time,
                    serves: singleRecipeData.serves
                }
            },
            {
                onSuccess: (res) => {
                    toast.success(res.message)
                    queryClient.invalidateQueries(recipesQueryKey())
                    queryClient.invalidateQueries(menusQueryKey())
                    navigate(-1)
                }
            }
        )
    }

    return (
        <UrlModal title='Edit Recipe' desc='Method' onClose={() => navigate(-1)}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <div>
                            {fields.map(({ id }, index) => {
                                return (
                                    <FormRow key={id}>
                                        <div className='relative'>
                                            <TextAreaFieldHookFormWrapper label={`Step ${index + 1}`} name={`method.${index}.value`} />
                                            <div className='absolute bottom-[0px] right-[-7px]'>
                                                <button
                                                    type='button'
                                                    onClick={() => remove(index)}
                                                    className='h-8 w-8 bg-secondary text-white flex justify-center items-center rounded-full'
                                                >
                                                    <TrashIcon className='w-5 text-error' />
                                                </button>
                                            </div>
                                        </div>
                                    </FormRow>
                                )
                            })}
                            {fields.length === 0 && <p>Press the plus button to add a method step.</p>}
                            <div className='w-full flex justify-end'>
                                <button type='button' onClick={() => append({ value: '' })}>
                                    <PlusIcon className='w-8 text-primary hover:text-primary-hover' />
                                </button>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter buttons={[<SubmitButton key={2} isSubmitting={isSubmitting} isValid={isValid} isDirty={true} text='Done' />]} />
                </form>
            </FormProvider>
        </UrlModal>
    )
}
